import React from 'react';
import { Box, Typography, Grid, Link, Container, IconButton, Divider } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import GitHubIcon from '@mui/icons-material/GitHub';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#001E0F', // Darker, richer green
        color: '#e0e0e0',
        py: 8,
        borderTop: '1px solid rgba(26,158,127,0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Graphic */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '5px', background: 'linear-gradient(90deg, #1A9E7F 0%, #034522 100%)' }} />
      <Box sx={{ position: 'absolute', bottom: '-10%', right: '-5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(26,158,127,0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6}>
          {/* Column 1: Brand & Social */}
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Box component="img" src="/logo.png" sx={{ height: 45, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', letterSpacing: -0.5 }}>
                GrowAITech
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 4, opacity: 0.8 }}>
              Pioneering the future of digital transformation. We bridge the gap between complex technology and business growth with elegant, scalable solutions.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {[
                { icon: <LinkedInIcon fontSize="small" />, href: "https://www.linkedin.com/company/growaitech" },
                { icon: <TwitterIcon fontSize="small" />, href: "https://x.com/GrowAiTech" },
                { icon: <GitHubIcon fontSize="small" />, href: "https://github.com/growaitech" },
                { icon: <InstagramIcon fontSize="small" />, href: "https://www.instagram.com/growaitech" },
                { icon: <FacebookIcon fontSize="small" />, href: "https://www.facebook.com/people/growaitech-company/61568267609033/" }
              ].map((social, idx) => (
                <IconButton
                  key={idx}
                  component="a"
                  href={social.href}
                  target="_blank"
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: '#1A9E7F', borderColor: '#1A9E7F', transform: 'translateY(-3px)' },
                    transition: 'all 0.3s',
                    width: 40, height: 40
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid item xs={6} md={2}>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, mb: 3, letterSpacing: 1 }}>
              COMPANY
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {[
                { name: 'About Us', path: '/about' },
                { name: 'Careers', path: '/careers' }, // Added for pro feel
                { name: 'Services', path: '/services' },
                { name: 'News & Media', path: '/news' }, // Added for pro feel
                { name: 'Contact', path: '/contact' }
              ].map((item) => (
                <Link
                  key={item.name}
                  href={item.path}
                  underline="none"
                  color="inherit"
                  sx={{
                    fontSize: '0.95rem',
                    opacity: 0.7,
                    transition: '0.3s',
                    '&:hover': { color: '#1A9E7F', opacity: 1, paddingLeft: '5px' }
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 3: Services */}
          <Grid item xs={6} md={3}>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, mb: 3, letterSpacing: 1 }}>
              SOLUTIONS
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {[
                { name: 'Web Development', link: '/services/web-development' },
                { name: 'AI & Machine Learning', link: '/services/artificial-intelligence' },
                { name: 'Data Analytics', link: '/services/data-analytics' },
                { name: 'Cloud Infrastructure', link: '/services/cloud-computing' },
                { name: 'Cybersecurity', link: '/services/cybersecurity' }
              ].map((service) => (
                <Link
                  key={service.name}
                  href={service.link}
                  underline="none"
                  color="inherit"
                  sx={{
                    fontSize: '0.95rem',
                    opacity: 0.7,
                    transition: '0.3s',
                    '&:hover': { color: '#1A9E7F', opacity: 1, paddingLeft: '5px' }
                  }}
                >
                  {service.name}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 4: Contact & Newsletter */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, mb: 3, letterSpacing: 1 }}>
              STAY CONNECTED
            </Typography>
            <Box display="flex" flexDirection="column" gap={2} mb={4}>
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <EmailIcon fontSize="small" sx={{ color: '#1A9E7F', mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>growaitech@gmail.com</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>admin@growaitech.com</Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1.5}>
                <PhoneIcon fontSize="small" sx={{ color: '#1A9E7F' }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>+91 63745 49599</Typography>
              </Box>
              <Box display="flex" alignItems="start" gap={1.5}>
                <LocationOnIcon fontSize="small" sx={{ color: '#1A9E7F', mt: 0.3 }} />
                <Link
                  href="https://maps.app.goo.gl/aFdQrvXh5Y8Ddn5K6"
                  target="_blank"
                  color="inherit"
                  underline="hover"
                  sx={{ opacity: 0.8, lineHeight: 1.5, '&:hover': { color: '#1A9E7F' } }}
                >
                  Door No. 61-4, S.F. No: 25/12 A 2B, Salem Bangalore Byepass Road, Salem, Tamil Nadu 636005
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6, borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* Bottom Bar */}
        <Box display="flex" flexDirection={{ xs: 'column-reverse', md: 'row' }} justifyContent="space-between" alignItems="center" gap={2}>
          <Typography variant="body2" sx={{ opacity: 0.5, fontSize: '0.85rem' }}>
            © {new Date().getFullYear()} GrowAITech Company. All Rights Reserved.
          </Typography>
          <Box display="flex" gap={4}>
            <Link href="/privacy-policy" underline="none" sx={{ color: '#fff', opacity: 0.5, fontSize: '0.85rem', '&:hover': { opacity: 1 } }}>Privacy Policy</Link>
            <Link href="/terms-of-service" underline="none" sx={{ color: '#fff', opacity: 0.5, fontSize: '0.85rem', '&:hover': { opacity: 1 } }}>Terms of Service</Link>
            <Link href="#" underline="none" sx={{ color: '#fff', opacity: 0.5, fontSize: '0.85rem', '&:hover': { opacity: 1 } }}>Cookie Policy</Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
